import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { PlayArrow, Stop, CheckCircle } from '@mui/icons-material';
import { getApiUrl } from '../utils/apiConfig';

// Öffentliche Scan-Buchung ohne Login: PIN identifiziert den Mitarbeiter.
// Bewusst minimal gehalten – nach dem Buchen wird nur "Gebucht ✓" angezeigt.
export default function ScanBuchungPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState(null);
  const [done, setDone] = useState(false);
  const [pin, setPin] = useState('');
  const [selectedAction, setSelectedAction] = useState('checkin');
  const [dauer, setDauer] = useState('');
  const [einheit, setEinheit] = useState('minuten');

  const api = useMemo(() => axios.create(), []);

  const loadInfo = useCallback(async () => {
    if (!token) {
      setError('Kein Scan-Code in der URL gefunden');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(getApiUrl('/api/scan/info'), { params: { token } });
      if (!data?.success) {
        throw new Error(data?.error || 'Scan-Code konnte nicht geprüft werden');
      }
      setInfo(data);
      setSelectedAction(data?.suggested_action || 'checkin');
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Scan-Code konnte nicht geprüft werden');
    } finally {
      setLoading(false);
    }
  }, [api, token]);

  useEffect(() => {
    loadInfo();
  }, [loadInfo]);

  const handleSubmit = async () => {
    if (!/^\d{4}$|^\d{6}$/.test(pin)) {
      setError('Bitte die PIN eingeben (4 oder 6 Ziffern)');
      return;
    }
    if (selectedAction === 'manual') {
      const value = Number(String(dauer).replace(',', '.'));
      if (!Number.isFinite(value) || value <= 0) {
        setError('Bitte eine gültige Dauer eingeben');
        return;
      }
    }
    setSubmitting(true);
    setError('');
    try {
      const { data } = await api.post(getApiUrl('/api/scan/zeitbuchungen'), {
        token,
        pin,
        action: selectedAction,
        dauer: selectedAction === 'manual' ? dauer : undefined,
        einheit: selectedAction === 'manual' ? einheit : undefined
      });
      if (!data?.success) {
        throw new Error(data?.error || 'Buchung fehlgeschlagen');
      }
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Buchung fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Card sx={{ width: '100%', maxWidth: 420 }}>
        <CardContent>
          <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center' }}>
            {loading ? (
              <CircularProgress />
            ) : done ? (
              <>
                <CheckCircle color="success" sx={{ fontSize: 80 }} />
                <Typography variant="h4">Gebucht ✓</Typography>
                <Typography color="text.secondary">Du kannst diese Seite jetzt schließen.</Typography>
              </>
            ) : (
              <>
                <Typography variant="overline" color="text.secondary">Zeiterfassung</Typography>
                <Typography variant="h5">{info?.task_titel || 'Task'}</Typography>
                {error ? <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert> : null}

                <TextField
                  label="Deine PIN"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6, style: { textAlign: 'center', fontSize: '1.6rem', letterSpacing: '0.5em' } }}
                  fullWidth
                  autoFocus
                />

                <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                  <Button fullWidth size="small" variant={selectedAction === 'checkin' ? 'contained' : 'outlined'} color="success" onClick={() => setSelectedAction('checkin')}>
                    Start
                  </Button>
                  <Button fullWidth size="small" variant={selectedAction === 'checkout' ? 'contained' : 'outlined'} color="error" onClick={() => setSelectedAction('checkout')}>
                    Ende
                  </Button>
                  <Button fullWidth size="small" variant={selectedAction === 'manual' ? 'contained' : 'outlined'} onClick={() => setSelectedAction('manual')}>
                    Zeit buchen
                  </Button>
                </Stack>

                {selectedAction === 'manual' ? (
                  <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                    <TextField
                      label="Dauer"
                      type="number"
                      value={dauer}
                      onChange={(e) => setDauer(e.target.value)}
                      inputProps={{ inputMode: 'decimal', min: 0 }}
                      fullWidth
                    />
                    <TextField select label="Einheit" value={einheit} onChange={(e) => setEinheit(e.target.value)} sx={{ minWidth: 130 }}>
                      <MenuItem value="minuten">Minuten</MenuItem>
                      <MenuItem value="stunden">Stunden</MenuItem>
                    </TextField>
                  </Stack>
                ) : null}

                <Button
                  fullWidth
                  size="large"
                  variant="contained"
                  color={selectedAction === 'checkout' ? 'error' : 'success'}
                  startIcon={selectedAction === 'checkin' ? <PlayArrow /> : selectedAction === 'checkout' ? <Stop /> : null}
                  disabled={submitting || pin.length !== 4}
                  onClick={handleSubmit}
                  sx={{ py: 1.5, fontSize: '1.05rem' }}
                >
                  {submitting
                    ? 'Bitte warten...'
                    : selectedAction === 'checkin'
                      ? 'Jetzt einstempeln'
                      : selectedAction === 'checkout'
                        ? 'Jetzt ausstempeln'
                        : 'Zeit buchen'}
                </Button>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
